/**
 * /zh-tw/decision-log — 決策紀錄 concept page (Traditional Chinese).
 *
 * The Draftlize wedge concept. Links to /zh-tw/meeting-minutes-ai bridge.
 */
import { MarketingShell } from './MarketingShell';

export function ZhTwDecisionLog() {
  return (
    <MarketingShell
      currentLocale="zhtw"
      hreflangPath="/decision-log"
      hreflangLocales={['en', 'zh', 'zhtw']}
      documentTitle="決策紀錄（Decision Log）是什麼｜不會腐爛的做法 · Draftlize"
      documentDescription="決策紀錄幫你留住「為什麼這樣決定」。多數決策紀錄寫完就沒人看、悄悄過時。教你把決策變成卡片、改變時自動標記 stale 的「活的」決策紀錄。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 決策紀錄</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            決策紀錄——<br />
            <em>回答「當初為什麼這樣決定？」</em>
          </h1>
          <p className="lede">
            決策紀錄（decision log）是一本帳，記下產品每個決定「<em>為什麼</em>這樣定」。幾個月後有人又把同一件事拿出來討論時，只要當時的脈絡、選項、取捨都在，就能一秒定案。問題是，大多數決策紀錄會<em>腐爛</em>——寫一次、沒人回看、悄悄過時。這頁教你怎麼寫，以及怎麼讓它「活著」。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">決策紀錄要記什麼。</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>決策</strong>——決定了什麼，一句話講清。</li>
            <li><strong>脈絡</strong>——為什麼此刻需要這個決定。</li>
            <li><strong>選項與取捨</strong>——考慮過什麼、為什麼捨棄其他。<em>這是日後最有用的部分</em>。</li>
            <li><strong>負責人與日期</strong>——誰、什麼時候。</li>
            <li><strong>影響範圍</strong>——牽動哪些規格或決策。</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            這個格式和 ADR（架構決策紀錄）相通，可直接抄走的欄位版見<a href="/zh-tw/decision-log-template" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>決策紀錄範本</a>。多數決策來自會議——先從 <a href="/zh-tw/meeting-minutes-ai" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>AI 會議記錄</a> 裡把決策抽出來，是第一步。
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">為什麼決策紀錄會腐爛。<em>以及，怎麼讓它不腐。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            純文字的決策紀錄，從寫下那刻就開始和現實脫節。後面的決定推翻了前面的，文件卻默默維持舊的樣子。在 Draftlize，決策是「活的卡片」。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">決策是可引用的卡片</h3>
            <p>一個決策＝一張卡片，帶著脈絡、選項、理由、負責人，用 ID 就能引用。不再埋在某份文件裡，隨時指得出來。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">被推翻的決策自動 stale</h3>
            <p>新決策推翻舊的，連結著的舊決策和它上面的規格會自動標記 stale。下次不會再重議「已經拍板的事」。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI agent 讀得也寫得</h3>
            <p>決策紀錄成為 Claude Code、Cursor 透過 MCP 讀取的脈絡。agent 帶著「為什麼這樣決定」往下寫。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          大多數決策紀錄，寫完隔天就沒人看。所以半年後，同樣的爭論又從零開始。
        </blockquote>
        <cite>把決策連著脈絡留下，改變時自動標出影響範圍。這就是 Draftlize。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常見問題</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '決策紀錄和會議記錄差在哪？', a: '會議記錄是「會議的記錄」，決策紀錄是「決策的帳本」。從會議記錄裡把決策抽出來累積，就是決策紀錄，日後真正有用的是後者。' },
            { q: '誰該負責寫？', a: '通常由對該決策負責的人（PM 或負責人）維護。重點是讓決策的「為什麼」日後追得回來。' },
            { q: '要寫多詳細？', a: '講清楚決策、脈絡、取捨就夠了。比起長度，「被推翻時能被提醒」的機制更重要。' },
            { q: '和 ADR 一樣嗎？', a: 'ADR 是技術決策紀錄的一種。Draftlize 把 ADR 和產品決策都當成同一種「活的卡片」處理。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">一本不會腐爛的決策紀錄<em>——免費送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新註冊送 $5</span>
          <span>用多少付多少</span>
          <span>餘額永不過期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            把決策連著脈絡變成卡片，被推翻就自動 stale，AI agent 也讀得到。用 Draftlize 建一本不會腐爛的決策紀錄。
          </p>
          <a className="btn-primary" href="/">免費開始，送 $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
